import { ReactNode } from "react";
import type { StepState } from "@core/features/onboarding";

export type Props = {
  /** Computed state for each step */
  stepStates: StepState[];
  /** Whether the current step can be skipped */
  canSkip: boolean;
  /** Whether this is the last step */
  isLastStep: boolean;
  /** Callback when a step is clicked */
  onStepClick?: (index: number) => void;
  /** Callback when skip is clicked */
  onSkipClick?: () => void;
  /** Callback when continue is clicked */
  onContinueClick?: () => void;
  /** Render function for each step item */
  renderStep: (stepState: StepState) => ReactNode;
  /** Render function for action buttons (continue/skip) */
  renderActions?: (props: {
    canSkip: boolean;
    isLastStep: boolean;
    onSkipClick?: () => void;
    onContinueClick?: () => void;
  }) => ReactNode;
  /** Additional CSS classes for the container */
  className?: string;
  /** Additional CSS classes for the steps list */
  stepsClassName?: string;
  /** Test ID for testing */
  "data-testid"?: string;
};

/**
 * Base stepper component for onboarding flows.
 *
 * This is an unstyled component that handles the structure of the stepper.
 * Use render props to customize step appearance and action buttons.
 *
 * @example
 * // In themes/pollinator/ui/OnboardingStepper.tsx
 * import { OnboardingStepperBase, type Props as OnboardingStepperBaseProps } from "@core/ui";
 * import { Button } from "../Button";
 * import { Type } from "../Type";
 *
 * export function OnboardingStepper({ stepStates, canSkip, isLastStep, ...props }) {
 *   return (
 *     <OnboardingStepperBase
 *       stepStates={stepStates}
 *       canSkip={canSkip}
 *       isLastStep={isLastStep}
 *       renderStep={(stepState) => (
 *         <Button
 *           variant="ghost"
 *           onClick={() => stepState.isClickable && props.onStepClick?.(stepState.index)}
 *           disabled={!stepState.isClickable}
 *           className={twMerge(
 *             "flex items-center gap-3",
 *             stepState.isCurrent && "bg-brand-100"
 *           )}
 *         >
 *           <Type>{stepState.step.slug}</Type>
 *         </Button>
 *       )}
 *       renderActions={({ canSkip, isLastStep, onSkipClick, onContinueClick }) => (
 *         <div className="flex gap-3">
 *           {onContinueClick && (
 *             <Button onClick={onContinueClick}>
 *               {isLastStep ? "Complete" : "Continue"}
 *             </Button>
 *           )}
 *           {canSkip && onSkipClick && (
 *             <Button variant="ghost" onClick={onSkipClick}>
 *               Skip for now
 *             </Button>
 *           )}
 *         </div>
 *       )}
 *       {...props}
 *     />
 *   );
 * }
 */
export function OnboardingStepperBase({
  stepStates,
  canSkip,
  isLastStep,
  onStepClick,
  onSkipClick,
  onContinueClick,
  renderStep,
  renderActions,
  className,
  stepsClassName,
  "data-testid": testId = "onboarding-stepper",
}: Props) {
  return (
    <div className={className} data-testid={testId}>
      {/* Steps list */}
      <div className={stepsClassName} data-testid={`${testId}-steps`}>
        {stepStates.map((stepState) => (
          <div
            key={stepState.step.slug}
            data-testid={`${testId}-step-${stepState.index}`}
            onClick={() => onStepClick?.(stepState.index)}
          >
            {renderStep(stepState)}
          </div>
        ))}
      </div>

      {/* Action buttons */}
      {renderActions && (onContinueClick || canSkip) && (
        <div data-testid={`${testId}-actions`}>
          {renderActions({
            canSkip,
            isLastStep,
            onSkipClick,
            onContinueClick,
          })}
        </div>
      )}
    </div>
  );
}

OnboardingStepperBase.displayName = "OnboardingStepperBase";
